"use client";

import * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, AlertTriangle, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CircularLoading } from "@/components/shared/circular-loading";
import { ALLOWED_PAGE_SIZES } from "@/types/filters";
import type { RouteAnalyticsSummary } from "@/types/route-analytics";
import { cn } from "@/lib/utils";
import { buildExportFilename, buildRouteAnalyticsCsv } from "@/utils/export-utils";
import { buildPaginationItems } from "@/utils/pagination";
import { formatStoredTimestamp } from "@/utils/date-utils";

type SortField = "route" | "totalRequests" | "successCount" | "errorCount" | "lastActivityAt";

interface RouteAnalyticsTableProps {
  rows: RouteAnalyticsSummary[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  /** Called when a row is clicked, with route name and total request count. */
  onRowClick: (route: string, totalRequests: number) => void;
  resultMayBePartial: boolean;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const COLUMNS: { id: SortField; label: string; className?: string }[] = [
  { id: "route", label: "Route", className: "w-[40%]" },
  { id: "totalRequests", label: "Total", className: "w-[12%]" },
  { id: "successCount", label: "Success", className: "w-[12%]" },
  { id: "errorCount", label: "Errors", className: "w-[12%]" },
  { id: "lastActivityAt", label: "Last activity", className: "w-[24%]" },
];

function downloadCsv(rows: RouteAnalyticsSummary[]) {
  const csv = buildRouteAnalyticsCsv(rows);
  const filename = buildExportFilename("dynamodb-dashboard-route-analytics");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function cellValue(row: RouteAnalyticsSummary, field: SortField): React.ReactNode {
  switch (field) {
    case "route":
      return (
        <span className="block truncate font-mono text-xs" title={row.route}>
          {row.route}
        </span>
      );
    case "totalRequests":
      return <span className="tabular-nums">{row.totalRequests.toLocaleString()}</span>;
    case "successCount":
      return (
        <span className="tabular-nums text-emerald-700 dark:text-emerald-400">
          {row.successCount.toLocaleString()}
        </span>
      );
    case "errorCount":
      return (
        <span className="tabular-nums text-red-700 dark:text-red-400">
          {row.errorCount.toLocaleString()}
        </span>
      );
    case "lastActivityAt":
      return (
        <span className="tabular-nums">
          {row.lastActivityAt ? (formatStoredTimestamp(row.lastActivityAt) ?? "—") : "—"}
        </span>
      );
  }
}

export function RouteAnalyticsTable({
  rows,
  isLoading,
  error,
  onRetry,
  onRowClick,
  resultMayBePartial,
  pageSize,
  onPageSizeChange,
  currentPage,
  onPageChange,
}: RouteAnalyticsTableProps) {
  const [sortField, setSortField] = React.useState<SortField>("totalRequests");
  const [sortDesc, setSortDesc] = React.useState(true);

  const sortedRows = React.useMemo(() => {
    const dir = sortDesc ? -1 : 1;
    return [...rows].sort((a, b) => {
      switch (sortField) {
        case "route":
          return dir * a.route.localeCompare(b.route);
        case "lastActivityAt":
          return dir * (a.lastActivityAt ?? "").localeCompare(b.lastActivityAt ?? "");
        default:
          return dir * (a[sortField] - b[sortField]);
      }
    });
  }, [rows, sortField, sortDesc]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize) || 1);
  const safePage = Math.min(Math.max(1, currentPage), pageCount);
  const pageRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const paginationItems = buildPaginationItems(safePage, pageCount);

  React.useEffect(() => {
    if (currentPage > pageCount) {
      onPageChange(pageCount);
    }
  }, [currentPage, pageCount, onPageChange]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDesc((prev) => !prev);
    } else {
      setSortField(field);
      setSortDesc(true);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {resultMayBePartial ? (
            <span className="flex items-center gap-1 text-xs text-warning" role="status">
              <AlertTriangle className="size-3.5" aria-hidden="true" />
              This result may be partial for the current filter combination.
            </span>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {sortedRows.length.toLocaleString()} route{sortedRows.length === 1 ? "" : "s"}
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={() => {
            if (sortedRows.length === 0) return;
            try {
              downloadCsv(sortedRows);
              toast.success(`Downloaded ${sortedRows.length} route${sortedRows.length === 1 ? "" : "s"}`);
            } catch {
              toast.error("Download failed. Please try again.");
            }
          }}
          disabled={isLoading || sortedRows.length === 0}
        >
          <Download className="size-3.5" aria-hidden="true" />
          Download CSV
        </Button>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border">
        {isLoading ? <CircularLoading overlay label="Loading route analytics…" size="lg" /> : null}
        {error ? (
          <div className="absolute inset-0 z-20 flex min-h-[12rem] items-center justify-center bg-background/85 p-6 backdrop-blur-[1px]">
            <ErrorState title="Could not load route analytics" onRetry={onRetry} />
          </div>
        ) : null}
        <div
          className={cn("absolute inset-0 overflow-auto", (isLoading || error) && "opacity-50")}
          tabIndex={0}
          aria-label="Route analytics table"
        >
          {!isLoading && !error && pageRows.length === 0 ? (
            <div className="flex min-h-[16rem] items-center justify-center p-6">
              <EmptyState title="No routes found" description="Try a different time range or clear filters." />
            </div>
          ) : (
            <table className="w-full caption-bottom text-sm" style={{ tableLayout: "fixed" }}>
              <thead className="sticky top-0 z-10 bg-background">
                <tr className="border-b">
                  {COLUMNS.map((column) => {
                    const isSorted = sortField === column.id;
                    return (
                      <th
                        key={column.id}
                        scope="col"
                        className={cn(
                          "overflow-hidden whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground",
                          column.className,
                        )}
                      >
                        <button
                          type="button"
                          className="flex items-center gap-1 outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                          {isSorted ? (
                            sortDesc ? (
                              <ArrowDown className="size-3" aria-hidden="true" />
                            ) : (
                              <ArrowUp className="size-3" aria-hidden="true" />
                            )
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" aria-hidden="true" />
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={row.route}
                    className="cursor-pointer border-b hover:bg-muted/50"
                    onClick={() => onRowClick(row.route, row.totalRequests)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row.route, row.totalRequests);
                      }
                    }}
                    tabIndex={0}
                  >
                    {COLUMNS.map((column) => (
                      <td key={column.id} className="overflow-hidden px-2 py-2 text-xs">
                        {cellValue(row, column.id)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number.parseInt(value, 10))}
          >
            <SelectTrigger className="h-8 w-[4.5rem] text-xs" aria-label="Page size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-4" />
          </Button>
          {paginationItems.map((item, index) =>
            item === "ellipsis" ? (
              <span key={`e-${index}`} className="px-1 text-xs text-muted-foreground">
                …
              </span>
            ) : (
              <Button
                key={item}
                type="button"
                variant={item === safePage ? "default" : "outline"}
                size="sm"
                className="h-8 min-w-8 px-2 text-xs"
                onClick={() => onPageChange(item)}
              >
                {item}
              </Button>
            ),
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8"
            disabled={safePage >= pageCount}
            onClick={() => onPageChange(safePage + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div aria-hidden="true" />
      </div>
    </div>
  );
}
